import { CommonModule, DatePipe } from '@angular/common'
import { Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { ServerRepresentedEventTiming } from '@app/modules/events/models/event.model'
import { PersonaFieldTypes } from '@app/modules/personas/enums/persona-fields-types.enum'
import { PersonaField } from '@app/modules/personas/interfaces/persona-field.interface'
import { RepositoriesService, country } from '@app/services/repositories.service'
import { NbButtonModule, NbCheckboxModule, NbInputModule } from '@nebular/theme'

export interface ColumnFilter {
  field: string
  values: string[]
  type: 'multi-select' | 'text' | 'numeric-range' | 'date-range' | 'has-content'
  inverted?: boolean
}

export interface FilterOption {
  value: string
  label: string
  selected: boolean
}

// Extended filter option with flag support
export interface FilterOptionWithFlag extends FilterOption {
  flag?: string
  phoneCode?: string
}

@Component({
  selector: 'app-column-filter-dropdown',
  templateUrl: './column-filter-dropdown.component.html',
  styleUrls: ['./column-filter-dropdown.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatCheckboxModule, MatIconModule, NbButtonModule, NbCheckboxModule, NbInputModule]
})
export class ColumnFilterDropdownComponent {
  field: string = ''
  fieldLabel: string = ''
  formElement: PersonaField | null = null
  existingValues: string[] = [] // Distinct values from data for suggestions
  selectedValues: string[] = []
  isInverted: boolean = false // Whether the existing filter is inverted
  eventTimings: ServerRepresentedEventTiming[] = [] // Event timings to determine if a day is today

  /** Wired by parent via `componentWithDataSetter` */
  onFilterChange?: (filter: ColumnFilter) => void
  onClose?: () => void

  private datePipe = inject(DatePipe)

  filterType: 'multi-select' | 'text' | 'numeric-range' | 'date-range' | 'has-content' = 'text'
  /** For has-content filter: true = has content, false = no content */
  hasContentValue: boolean | null = null
  options: FilterOptionWithFlag[] = []
  searchText: string = ''
  textFilterValue: string = ''
  numericMin: string = ''
  numericMax: string = ''
  /** Date range filter values */
  dateFrom: string = ''
  dateTo: string = ''
  /** Whether to invert the filter (NOT IN instead of IN) */
  invertFilter: boolean = false

  /** Confirmation email status is include-only; exclude is not offered. */
  get allowsInvert(): boolean {
    return this.field !== 'mail_status' && this.field !== '#email'
  }

  // For multi-select search filtering
  filteredOptions: FilterOptionWithFlag[] = []

  // Countries from the repository service
  private countries: country[] = []

  constructor(private repoService: RepositoriesService) {
    this.countries = this.repoService.CountriesTimezonesRepository || []
  }

  /** Called after parent sets filter context via shared dropdown `dataSetter`. */
  initFromData(): void {
    this.invertFilter = this.allowsInvert && this.isInverted
    this.determineFilterType()
    this.buildOptions()
    this.filteredOptions = [...this.options]
  }

  private determineFilterType(): void {
    // First check by field name for common field patterns
    const fieldLower = this.field?.toLowerCase() || ''

    // System fields - multi-select
    if (this.field === 'mail_status' || this.field === '#email') {
      this.filterType = 'multi-select'
      return
    }

    // SMS status field
    if (this.field === 'sms_status' || this.field === '#sms') {
      this.filterType = 'multi-select'
      return
    }

    // WhatsApp status field
    if (this.field === 'whatsapp_status' || this.field === '#whatsapp') {
      this.filterType = 'multi-select'
      return
    }

    // Attendance field
    if (this.field === 'attended' || this.field === '#attended' || this.field === '#attend' || fieldLower.includes('attendance')) {
      this.filterType = 'multi-select'
      return
    }

    // Day attendance columns (e.g., #day1, #day2, etc.)
    if (this.field.match(/^#day\d+$/)) {
      this.filterType = 'multi-select'
      return
    }

    // Approval status field
    if (this.field === '#approve' || this.field === 'entry_status') {
      this.filterType = 'multi-select'
      return
    }

    // Approved/Declined by field - text filter
    if (this.field === '#approved_by' || this.field === 'approved_by') {
      this.filterType = 'text'
      return
    }

    // Date fields - use date-range filter
    if (this.field === 'created_at' || this.field === '#created_at' || fieldLower.includes('date') || fieldLower.includes('_at')) {
      this.filterType = 'date-range'
      return
    }

    // If no form element, try to determine type from field name
    if (!this.formElement) {
      // Common field name patterns
      if (fieldLower.includes('country')) {
        this.filterType = 'multi-select'
        return
      }
      if (fieldLower.includes('phone')) {
        this.filterType = 'multi-select'
        return
      }
      if (fieldLower.includes('gender')) {
        this.filterType = 'multi-select'
        return
      }
      // Default to text for unknown fields
      this.filterType = 'text'
      return
    }

    const typeId = this.formElement.form_element_type_id

    switch (typeId) {
      case PersonaFieldTypes.COUNTRY:
      case PersonaFieldTypes.GENDER:
      case PersonaFieldTypes.AGE_GROUP:
      case PersonaFieldTypes.RADIO:
      case PersonaFieldTypes.CHECKBOX:
      case PersonaFieldTypes.PHONE: // Phone uses country code multi-select
        this.filterType = 'multi-select'
        break
      case PersonaFieldTypes.DATE:
        this.filterType = 'date-range'
        break
      case PersonaFieldTypes.NUMERIC:
        this.filterType = 'numeric-range'
        break
      case PersonaFieldTypes.UPLOAD:
        this.filterType = 'has-content'
        break
      case PersonaFieldTypes.EMAIL:
      case PersonaFieldTypes.FULLNAME:
      case PersonaFieldTypes.ORGANIZATION:
      case PersonaFieldTypes.JOB_TITLE:
      case PersonaFieldTypes.PLAIN_TEXT:
      case PersonaFieldTypes.URL:
      case PersonaFieldTypes.CITY:
      default:
        this.filterType = 'text'
        break
    }
  }

  private buildOptions(): void {
    const fieldLower = this.field?.toLowerCase() || ''

    // For date-range filter, pre-fill dates if there are existing values
    // Values are stored as "from:to" format
    if (this.filterType === 'date-range') {
      if (this.selectedValues.length > 0 && this.selectedValues[0]) {
        const dateValue = this.selectedValues[0]
        if (dateValue.includes(':')) {
          const [from, to] = dateValue.split(':')
          this.dateFrom = from || ''
          this.dateTo = to || ''
        } else {
          this.dateFrom = dateValue
        }
      }
      return
    }

    // For numeric-range filter, pre-fill values
    // Values are stored as "min:max" format
    if (this.filterType === 'numeric-range') {
      if (this.selectedValues.length > 0 && this.selectedValues[0]) {
        const numericValue = this.selectedValues[0]
        if (numericValue.includes(':')) {
          const [min, max] = numericValue.split(':')
          this.numericMin = min || ''
          this.numericMax = max || ''
        } else {
          this.numericMin = numericValue
        }
      }
      return
    }

    // For has-content filter, pre-fill from existing values
    if (this.filterType === 'has-content') {
      if (this.selectedValues.length > 0) {
        if (this.selectedValues[0] === 'has_content') {
          this.hasContentValue = true
        } else if (this.selectedValues[0] === 'no_content') {
          this.hasContentValue = false
        }
      }
      return
    }

    if (this.filterType !== 'multi-select') {
      // For text filter, pre-fill if there's an existing value
      if (this.selectedValues.length > 0) {
        this.textFilterValue = this.selectedValues[0]
      }
      return
    }

    // Email confirmation status field
    if (this.field === 'mail_status' || this.field === '#email') {
      this.options = [
        { value: 'none', label: 'Not Sent', selected: this.selectedValues.includes('none') },
        { value: 'Sent', label: 'Sent', selected: this.selectedValues.includes('Sent') },
        { value: 'Delivery', label: 'Delivered', selected: this.selectedValues.includes('Delivery') },
        { value: 'Bounce', label: 'Bounced', selected: this.selectedValues.includes('Bounce') },
        { value: 'Complaint', label: 'Complaint', selected: this.selectedValues.includes('Complaint') },
        { value: 'error', label: 'Error', selected: this.selectedValues.includes('error') }
      ]
      return
    }

    // SMS status field
    if (this.field === 'sms_status' || this.field === '#sms') {
      this.options = [
        { value: 'none', label: 'Not Sent', selected: this.selectedValues.includes('none') },
        { value: 'Pending', label: 'Pending', selected: this.selectedValues.includes('Pending') },
        { value: 'Sent', label: 'Received', selected: this.selectedValues.includes('Received') },
        { value: 'Failed', label: 'Failed', selected: this.selectedValues.includes('Failed') },
        { value: 'Not Supported', label: 'Not Supported', selected: this.selectedValues.includes('Not Supported') }
      ]
      return
    }

    // WhatsApp status field
    if (this.field === 'whatsapp_status' || this.field === '#whatsapp') {
      // Backend returns lowercase in responses, but historically filters used TitleCase in this UI.
      // We accept both formats by offering TitleCase values (and "none").
      this.options = [
        { value: 'none', label: 'Not Sent', selected: this.selectedValues.includes('none') },
        { value: 'read', label: 'Read', selected: this.selectedValues.includes('read') },
        { value: 'sent', label: 'Sent', selected: this.selectedValues.includes('sent') },
        { value: 'delivered', label: 'Delivered', selected: this.selectedValues.includes('delivered') },
        { value: 'failed', label: 'Failed', selected: this.selectedValues.includes('failed') }
      ]
      return
    }

    // Attendance field
    if (this.field === 'attended' || this.field === '#attended' || this.field === '#attend' || fieldLower.includes('attendance')) {
      this.options = [
        { value: 'yes', label: 'Attended', selected: this.selectedValues.includes('yes') },
        { value: 'no', label: 'Not Attended', selected: this.selectedValues.includes('no') }
      ]
      return
    }

    // Day attendance columns (e.g., #day1, #day2, etc.)
    if (this.field.match(/^#day\d+$/)) {
      // Extract day number from field (e.g., #day1 -> 1)
      const dayMatch = this.field.match(/^#day(\d+)$/)
      const dayNumber = dayMatch ? parseInt(dayMatch[1], 10) - 1 : -1 // Convert to 0-based index

      // Check if this day is today
      let isToday = false
      if (dayNumber >= 0 && this.eventTimings && this.eventTimings.length > dayNumber) {
        const dayTiming = this.eventTimings[dayNumber]
        if (dayTiming?.start_datetime) {
          const dayDate = this.datePipe.transform(dayTiming.start_datetime, 'yyyy-MM-dd')
          const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd')
          isToday = dayDate === today
        }
      }

      // For today, show "Expected" instead of "Missed" (but keep "missed" as the value for API)
      const missedLabel = isToday ? 'Expected' : 'Missed'

      this.options = [
        { value: 'attended', label: 'Attended', selected: this.selectedValues.includes('attended') },
        { value: 'missed', label: missedLabel, selected: this.selectedValues.includes('missed') }
      ]
      return
    }

    // Approval status field - ordered by most common/important first
    if (this.field === '#approve' || this.field === 'entry_status') {
      this.options = [
        { value: '4', label: 'Approved', selected: this.selectedValues.includes('4') },
        { value: '5', label: 'Declined', selected: this.selectedValues.includes('5') },
        { value: '0', label: 'Pending', selected: this.selectedValues.includes('0') },
        { value: '2', label: 'Awaiting Revision', selected: this.selectedValues.includes('2') },
        { value: '3', label: 'Updated', selected: this.selectedValues.includes('3') },
        { value: '1', label: 'Pending Payment', selected: this.selectedValues.includes('1') }
      ]
      return
    }

    // Check if this is a country field (by type or by field name)
    const isCountryField = this.formElement?.form_element_type_id === PersonaFieldTypes.COUNTRY || (!this.formElement && fieldLower.includes('country'))
    if (isCountryField) {
      // Use the countries from RepositoriesService with flags
      this.options = this.countries.map((c) => ({
        value: c.name,
        label: c.name,
        selected: this.selectedValues.includes(c.name),
        flag: c.flag
      }))
      return
    }

    // Check if this is a phone field - use country codes with flags (by type or by field name)
    const isPhoneField = this.formElement?.form_element_type_id === PersonaFieldTypes.PHONE || (!this.formElement && fieldLower.includes('phone'))
    if (isPhoneField) {
      // Use the countries from RepositoriesService with phone codes and flags
      this.options = this.countries
        .filter((c) => c.phone_code) // Only countries with phone codes
        .map((c) => ({
          value: '+' + c.phone_code,
          label: c.name,
          selected: this.selectedValues.includes('+' + c.phone_code),
          flag: c.flag,
          phoneCode: '+' + c.phone_code
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      return
    }

    // For form elements with predefined values (radio, checkbox, gender, etc.)
    if (this.formElement?.form_element_values?.length > 0) {
      this.options = this.formElement.form_element_values.map((v) => ({
        value: v.slug || v.name,
        label: v.name,
        selected: this.selectedValues.includes(v.slug || v.name)
      }))
    } else if (this.existingValues.length > 0) {
      // For other fields, use distinct values from data
      this.options = this.existingValues.map((v) => ({
        value: v,
        label: v,
        selected: this.selectedValues.includes(v)
      }))
    }
  }

  onSearchChange(): void {
    if (!this.searchText.trim()) {
      this.filteredOptions = [...this.options]
      return
    }

    const search = this.searchText.toLowerCase()
    this.filteredOptions = this.options.filter(
      (opt) => opt.label.toLowerCase().includes(search) || opt.value.toLowerCase().includes(search) || (opt.phoneCode && opt.phoneCode.includes(search))
    )
  }

  toggleOption(option: FilterOption): void {
    option.selected = !option.selected
  }

  selectAll(): void {
    this.filteredOptions.forEach((opt) => (opt.selected = true))
    // Also update the main options array
    this.options.forEach((opt) => {
      const filtered = this.filteredOptions.find((f) => f.value === opt.value)
      if (filtered) opt.selected = filtered.selected
    })
  }

  clearAll(): void {
    this.options.forEach((opt) => (opt.selected = false))
    this.filteredOptions.forEach((opt) => (opt.selected = false))
    this.textFilterValue = ''
    this.numericMin = ''
    this.numericMax = ''
  }

  applyFilter(): void {
    let values: string[] = []

    switch (this.filterType) {
      case 'multi-select':
        values = this.options.filter((opt) => opt.selected).map((opt) => opt.value)
        break
      case 'text':
        if (this.textFilterValue.trim()) {
          values = [this.textFilterValue.trim()]
        }
        break
      case 'numeric-range':
        // For numeric range, we'll encode as "min:max" - backend will need to handle this
        if (this.numericMin || this.numericMax) {
          values = [`${this.numericMin || ''}:${this.numericMax || ''}`]
        }
        break
      case 'date-range':
        // For date range, we'll encode as "from:to" - backend will need to handle this
        if (this.dateFrom || this.dateTo) {
          values = [`${this.dateFrom || ''}:${this.dateTo || ''}`]
        }
        break
      case 'has-content':
        // For has-content filter, encode as 'has_content' or 'no_content'
        if (this.hasContentValue !== null) {
          values = [this.hasContentValue ? 'has_content' : 'no_content']
        }
        break
    }

    this.onFilterChange?.({
      field: this.field,
      values,
      type: this.filterType,
      inverted: this.allowsInvert && this.invertFilter
    })

    this.onClose?.()
  }

  toggleInvert(): void {
    this.invertFilter = !this.invertFilter
  }

  cancel(): void {
    this.onClose?.()
  }

  get hasActiveFilter(): boolean {
    switch (this.filterType) {
      case 'multi-select':
        return this.options.some((opt) => opt.selected)
      case 'text':
        return !!this.textFilterValue.trim()
      case 'numeric-range':
        return !!(this.numericMin || this.numericMax)
      case 'date-range':
        return !!(this.dateFrom || this.dateTo)
      case 'has-content':
        return this.hasContentValue !== null
      default:
        return false
    }
  }

  /** Set has-content filter value */
  setHasContent(value: boolean): void {
    this.hasContentValue = value
  }
}
